import React, { useMemo, useCallback } from 'react';
import { Box, Text, Loading, Clickable, Icon, FlagIcon, Avatar, Button } from '@maui/elements';
import { formatDistance, format, parseISO } from 'date-fns';
import { openModal, formatBytes, capitalizeString } from '@horizon/utilities';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Score } from '@maui/score';
import filesize from 'filesize';
import { Tooltip } from '@maui/elements-tooltip';
import { useEodDrawer } from '@horizon/hooks';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}

/**
 * Allowing rest props here, because I'd
 * like to be able to pass different
 * styles to this component e.g on
 * on the Archive View (NautilusJS)
 * this component will be inside
 * a container
 *
 */
var FileBox = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement(Box, __assign({ display: "flex", backgroundColor: "colorBackgroundSurface", padding: "spaceGapXLarge", flexDirection: "column", marginBottom: "spaceGapXLarge" }, props), children));
};

var FileHeader = function (_a) {
    var children = _a.children;
    return (React.createElement(Box, { display: "flex", alignItems: "center", justifyContent: "space-between" }, children));
};

var FileSubHeader = function (_a) {
    var children = _a.children;
    return (React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapLarge", marginTop: "spaceGapMedium", marginBottom: "spaceGapMedium" }, children));
};

var FileBody = function (_a) {
    var children = _a.children;
    return (React.createElement(Box, { display: "flex", gap: "spaceGapXxLarge", marginTop: "-6px" }, children));
};

var FileDate = function (_a) {
    var createdTime = _a.createdTime;
    var timeAgo = useMemo(function () {
        return formatDistance(new Date(createdTime), new Date(), { addSuffix: true });
    }, []);
    return (React.createElement(Text, { fontSize: "10px", fontWeight: "700", textTransform: "uppercase", lineHeight: "16px", letterSpacing: "2px", flexShrink: 0, marginLeft: "spaceGapXLarge" }, timeAgo));
};

var FileName = function (_a) {
    var filename = _a.filename, md5 = _a.md5, fetchingData = _a.fetchingData, malicious = _a.malicious;
    var color = malicious ? 'colorRiskCritical' : 'colorTextMain';
    var onClick = useCallback(function () {
        openModal(md5, 'md5', 'IndicatorModal');
    }, [md5]);
    return (React.createElement(React.Fragment, null, fetchingData ? (React.createElement(Box, { marginRight: "spaceGapMedium", display: "flex", alignItems: "center" },
        React.createElement(Loading, { size: "extraSmall" }))) : (React.createElement(Clickable, { color: color, _hover: {
            textDecoration: 'underline',
            color: color,
        }, flexGrow: 1, minWidth: 0, onClick: onClick },
        React.createElement(Text, { variant: "heading2", color: color, textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }, filename || 'N/A')))));
};

// Sorry Steve, stealing this from you
// https://ghe.eng.fireeye.com/GSI-SDE-FIIP/nautilusjs/blob/master/packages/component-core/src/FlagIcon/countryCodes.js
var isValidFlagIconCode = function (code) {
    return typeof code === 'string' && VALID_ISO2_CODES.includes(code.toLowerCase());
};
var VALID_ISO2_CODES = [
    'ad',
    'ae',
    'af',
    'ag',
    'ai',
    'al',
    'am',
    'ao',
    'aq',
    'ar',
    'as',
    'at',
    'au',
    'aw',
    'ax',
    'az',
    'ba',
    'bb',
    'bd',
    'be',
    'bf',
    'bg',
    'bh',
    'bi',
    'bj',
    'bl',
    'bm',
    'bn',
    'bo',
    'bq',
    'br',
    'bs',
    'bt',
    'bv',
    'bw',
    'by',
    'bz',
    'ca',
    'cc',
    'cd',
    'cf',
    'cg',
    'ch',
    'ci',
    'ck',
    'cl',
    'cm',
    'cn',
    'co',
    'cr',
    'cu',
    'cv',
    'cw',
    'cx',
    'cy',
    'cz',
    'de',
    'dj',
    'dk',
    'dm',
    'do',
    'dz',
    'ec',
    'ee',
    'eg',
    'eh',
    'er',
    'es-ca',
    'es',
    'et',
    'eu',
    'fi',
    'fj',
    'fk',
    'fm',
    'fo',
    'fr',
    'ga',
    'gb-eng',
    'gb-nir',
    'gb-sct',
    'gb-wls',
    'gb',
    'gd',
    'ge',
    'gf',
    'gg',
    'gh',
    'gi',
    'gl',
    'gm',
    'gn',
    'gp',
    'gq',
    'gr',
    'gs',
    'gt',
    'gu',
    'gw',
    'gy',
    'hk',
    'hm',
    'hn',
    'hr',
    'ht',
    'hu',
    'id',
    'ie',
    'il',
    'im',
    'in',
    'io',
    'iq',
    'ir',
    'is',
    'it',
    'je',
    'jm',
    'jo',
    'jp',
    'ke',
    'kg',
    'kh',
    'ki',
    'km',
    'kn',
    'kp',
    'kr',
    'kw',
    'ky',
    'kz',
    'la',
    'lb',
    'lc',
    'li',
    'lk',
    'lr',
    'ls',
    'lt',
    'lu',
    'lv',
    'ly',
    'ma',
    'mc',
    'md',
    'me',
    'mf',
    'mg',
    'mh',
    'mk',
    'ml',
    'mm',
    'mn',
    'mo',
    'mp',
    'mq',
    'mr',
    'ms',
    'mt',
    'mu',
    'mv',
    'mw',
    'mx',
    'my',
    'mz',
    'na',
    'nc',
    'ne',
    'nf',
    'ng',
    'ni',
    'nl',
    'no',
    'np',
    'nr',
    'nu',
    'nz',
    'om',
    'pa',
    'pe',
    'pf',
    'pg',
    'ph',
    'pk',
    'pl',
    'pm',
    'pn',
    'pr',
    'ps',
    'pt',
    'pw',
    'py',
    'qa',
    're',
    'ro',
    'rs',
    'ru',
    'rw',
    'sa',
    'sb',
    'sc',
    'sd',
    'se',
    'sg',
    'sh',
    'si',
    'sj',
    'sk',
    'sl',
    'sm',
    'sn',
    'so',
    'sr',
    'ss',
    'st',
    'sv',
    'sx',
    'sy',
    'sz',
    'tc',
    'td',
    'tf',
    'tg',
    'th',
    'tj',
    'tk',
    'tl',
    'tm',
    'tn',
    'to',
    'tr',
    'tt',
    'tv',
    'tw',
    'tz',
    'ua',
    'ug',
    'um',
    'un',
    'us',
    'uy',
    'uz',
    'va',
    'vc',
    've',
    'vg',
    'vi',
    'vn',
    'vu',
    'wf',
    'ws',
    'xk',
    'ye',
    'yt',
    'za',
    'zm',
    'zw',
];

var ActorFlag = function (_a) {
    var countryCode = _a.countryCode;
    if (!countryCode || !isValidFlagIconCode(countryCode)) {
        return React.createElement(Icon, { width: "10px", height: "12px", name: "user-secret" });
    }
    return (React.createElement(FlagIcon, { code: countryCode.toLowerCase() }));
};

/**
 * Original Pill component is broken currently,
 * this is a temporary solution this story is
 * solved:
 *
 * https://madvantage.atlassian.net/browse/MAPUI-873
 *
 */
var Pill = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement(Box, __assign({ fontFamily: "textPillFontFamily", fontSize: "textPillFontSize", backgroundColor: "colorActiveSecondary", color: "colorTextReverse", fontWeight: "textPillFontWeight", lineHeight: "textPillLineHeight", display: "inline-block", paddingLeft: "spaceGapMedium", paddingRight: "spaceGapMedium", paddingTop: "0", paddingBottom: "0", borderRadius: "radiusLarge" }, props), children));
};

var AttributionPill = function (_a) {
    var children = _a.children;
    return (React.createElement(Pill, { backgroundColor: "colorFillMain", color: "colorTextDeemphasis", textTransform: "uppercase", fontWeight: "700", lineHeight: "16px", borderWidth: "1px", borderStyle: "solid", borderColor: "colorBorderDeemphasis", display: "flex", alignItems: "center", gap: "spaceGapSmall" }, children));
};

var Copy = function (_a) {
    var text = _a.text;
    return (React.createElement(CopyToClipboard, { text: text },
        React.createElement(Box, { cursor: "pointer" },
            React.createElement(Icon, { name: "copy-clipboard", width: "12px", height: "12px", color: "colorActionPrimary" }))));
};

var SectionLabel = function (_a) {
    var children = _a.children;
    return (React.createElement(Text, { maxWidth: "100%", fontSize: "10px", fontWeight: "700", textTransform: "uppercase", lineHeight: "16px", letterSpacing: "2px", color: "colorTextDeemphasis", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }, children));
};

var FileType = function (_a) {
    var type = _a.type;
    return (React.createElement(Pill, { backgroundColor: "colorFillEmphasis", color: "colorTextDeemphasis", textTransform: "uppercase", fontWeight: "700", lineHeight: "16px", borderWidth: "1px", borderStyle: "solid", borderColor: "transparent" }, type));
};

var boxWidth = {
    width: '40%',
    maxWidth: '500px',
};
var FileScoreAndStatus = function (_a) {
    var score = _a.score, verdict = _a.verdict, reportStatus = _a.reportStatus, fetchingData = _a.fetchingData;
    if (fetchingData || reportStatus !== 'COMPLETED') {
        return (React.createElement(Box, __assign({}, boxWidth, { display: "flex", alignItems: "center", justifyContent: "center", marginTop: "spaceGapXLarge", gap: "spaceGapLarge" }),
            React.createElement(Loading, { label: fetchingData
                    ? false
                    : reportStatus === 'ANALYZING'
                        ? 'Analysis in Progress'
                        : 'Pending Analysis' })));
    }
    return (React.createElement(Box, __assign({}, boxWidth, { style: { '--maui-score-scale-border-width': '0px' } }),
        React.createElement(Score, { score: score, verdict: verdict, summaryOnly: true, scoreOnly: true, variant: "stacked", loadFonts: false })));
};

var FileMetadata = function (_a) {
    var size = _a.size, owner = _a.owner, mimeType = _a.mimeType, fetchingReport = _a.fetchingReport, type = _a.type;
    return (React.createElement(Box, { display: "flex", gap: "spaceGapXxLarge", marginTop: "spaceGapXLarge", paddingLeft: "spaceGapXxLarge", borderLeftWidth: "1px", borderLeftColor: "colorBorderMain", borderLeftStyle: "solid", width: "30%", maxWidth: "450px" },
        React.createElement(Box, { minWidth: "0", display: "flex", flexDirection: "column", gap: "spaceGapMedium", flexGrow: "1" },
            React.createElement(Box, { display: "flex", gap: "spaceGapXLarge" },
                React.createElement(Row$1, null,
                    React.createElement(SectionLabel, null, type === 'archive' ? 'Compressed Size' : 'Size'),
                    React.createElement(Value, null,
                        filesize(size, { standard: 'jedec', base: 1, round: 1 }),
                        " (",
                        formatBytes(size),
                        " Bytes)")),
                React.createElement(Row$1, null,
                    React.createElement(SectionLabel, null, "Submitted by"),
                    React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapSmall" },
                        React.createElement(Avatar, { flexShrink: 0, width: "12px", height: "12px", fontSize: "6px", fontWeight: "700" }, owner.initials),
                        React.createElement(Value, null, owner.name)))),
            React.createElement(Box, { display: "flex" },
                React.createElement(Row$1, null,
                    React.createElement(SectionLabel, null, "Mime Type"),
                    React.createElement(Value, null, fetchingReport ? (React.createElement(Box, { marginTop: "spaceGapTiny" },
                        React.createElement(Loading, { size: "extraSmall" }))) : (mimeType || 'N/A')))))));
};
var Value = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return (React.createElement(Text, __assign({ maxWidth: "100%", variant: "heading6", fontWeight: "700", whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }, props), children));
};
var Row$1 = function (_a) {
    var children = _a.children;
    return (React.createElement(Box, { gap: "spaceGapTiny", flex: "1", minWidth: "0" }, children));
};

var FileHashes = function (_a) {
    var sha256 = _a.sha256, sha1 = _a.sha1, md5 = _a.md5;
    return (React.createElement(Box, { display: "flex", flexDirection: "column", flex: "1", marginTop: "spaceGapXLarge", paddingX: "spaceGapXxLarge", borderLeftWidth: "1px", borderLeftColor: "colorBorderMain", borderLeftStyle: "solid", overflow: "hidden", minWidth: "0" },
        sha256 && (React.createElement(Row, null,
            React.createElement(Type, null, "SHA256"),
            React.createElement(Hash, null, sha256))),
        sha1 && (React.createElement(Row, null,
            React.createElement(Type, null, "SHA1"),
            React.createElement(Hash, null, sha1))),
        md5 && (React.createElement(Row, null,
            React.createElement(Type, null, "MD5"),
            React.createElement(Hash, null, md5)))));
};
var Type = function (_a) {
    var children = _a.children;
    return (React.createElement(Text, { minWidth: "55px", variant: "heading6", color: "colorTextDeemphasis", fontWeight: "700" }, children));
};
var Hash = function (_a) {
    var children = _a.children;
    return (React.createElement(Box, { display: "flex", gap: "spaceGapMedium", minWidth: "0" },
        React.createElement(Text, { color: "colorTextDeemphasis", fontFamily: "'Source Code Pro', monospace", minWidth: "0", textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden" }, children),
        React.createElement(Copy, { text: children })));
};
var Row = function (_a) {
    var children = _a.children;
    return (React.createElement(Box, { display: "flex", alignItems: "center", marginTop: "spaceGapTiny", flexDirection: "row", maxWidth: "100%", gap: "spaceGapSmall" }, children));
};

var FileMalwareFamilies = function (_a) {
    var fetchingData = _a.fetchingData, malwareFamilies = _a.malwareFamilies;
    var onClick = useCallback(function (id, name) {
        openModal(id, name, 'MalwareModal');
    }, []);
    if (fetchingData) {
        return React.createElement(Loading, { size: "extraSmall" });
    }
    if (!(malwareFamilies === null || malwareFamilies === void 0 ? void 0 : malwareFamilies.length)) {
        return null;
    }
    return (React.createElement(React.Fragment, null, malwareFamilies.map(function (family, idx) { return (React.createElement(Clickable, { onClick: function () { return onClick(family.id, family.name); }, key: idx },
        React.createElement(AttributionPill, null,
            React.createElement(Icon, { name: "bug", width: "10px", height: "12px", color: "colorRiskCritical" }),
            family.name))); })));
};

var FileAssistance = function (_a) {
    var children = _a.children;
    return (React.createElement(Box, { paddingLeft: "spacePaddingXLarge", marginLeft: "spaceGapMedium", borderLeftWidth: "1px", borderLeftColor: "colorBorderMain", borderLeftStyle: "solid", _first: {
            paddingLeft: 0,
            marginLeft: 0,
            borderLeft: 0,
        } }, children));
};

var FileContents = function (_a) {
    var extractedFilesCount = _a.extractedFilesCount;
    return (React.createElement(Box, { display: "flex", alignItems: "center", flexDirection: "column", paddingX: "spaceGapLarge", marginTop: "spaceGapXLarge" },
        React.createElement(SectionLabel, null, "CONTAINS"),
        React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapSmall" },
            React.createElement(Icon, { name: "blank-paper", size: "lg" }),
            React.createElement(Text, { variant: "heading1" }, extractedFilesCount))));
};

var FileThreatActor = function (_a) {
    var fetchingData = _a.fetchingData, actor = _a.actor;
    var onClick = useCallback(function () {
        if (!actor) {
            return;
        }
        openModal(actor.id, actor.name, 'ActorModal');
    }, [actor]);
    if (fetchingData) {
        return React.createElement(Loading, { size: "extraSmall" });
    }
    if (!actor) {
        return null;
    }
    return (React.createElement(Clickable, { onClick: onClick },
        React.createElement(AttributionPill, null,
            React.createElement(ActorFlag, { countryCode: actor.countryCode }),
            actor.name)));
};

var FileMaliciousContent = function (_a) {
    var type = _a.type, malicious = _a.malicious;
    if (type === 'file' || !malicious) {
        return null;
    }
    return (React.createElement(Pill, { backgroundColor: "colorFillMain", color: "colorTextMain", fontWeight: "700", lineHeight: "16px", borderWidth: "1px", borderStyle: "solid", borderColor: "colorBorderDeemphasis", display: "flex", alignItems: "center", gap: "spaceGapSmall" },
        React.createElement(Icon, { name: "alert-triangle", width: "14px", height: "12px", color: "colorRiskCritical" }),
        "Malicious Content"));
};

var FileAssistanceRequest = function (_a) {
    var filename = _a.filename, sha256 = _a.sha256, createdTime = _a.createdTime, size = _a.size;
    var payload = {
        file_name: filename,
        id: sha256,
        created_at: createdTime,
        file_size: size,
    };
    var openDrawer = useEodDrawer();
    var onClick = useCallback(function () {
        openDrawer(payload);
    }, [payload]);
    return (React.createElement(Button, { variant: "primaryText", textStyle: "bodySmall", textDecoration: "none", padding: "0", color: "colorActionSecondary", onClick: onClick },
        React.createElement(Tooltip, { textStyle: "bodyMain", instanceProps: {
                backgroundColor: 'colorBackgroundOverlay',
                color: 'colorTextMain',
            }, portal: true, tooltip: "Use EOD units or AA's to request  further research on this file" },
            React.createElement(Icon, { name: "hand-raised", size: "sm" }),
            "\u00A0 Ask an Expert")));
};

var FileAssistanceCase = function (_a) {
    var id = _a.id, timestamp = _a.timestamp;
    var onViewReport = useCallback(function () {
        openModal(id, 'View Report', 'ServiceDetailsModal');
    }, [id]);
    return (React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapMedium" },
        React.createElement(Icon, { name: "messages", color: "colorActionPrimary" }),
        React.createElement(Box, { display: "flex", alignItems: "center", gap: "spaceGapSmall" },
            React.createElement(Clickable, { onClick: onViewReport },
                React.createElement(Text, { variant: "heading5", color: "colorActivePrimary" }, "Investigation Requested")),
            React.createElement(Text, { variant: "body1", color: "colorTextDeemphasis" }, timestamp))));
};

var iconsByCampaign = {
    Individual: "bullseye",
    Global: "tri-bullseye"
};
var FileCampaign = function (_a) {
    var campaign = _a.campaign, fetchingCampaign = _a.fetchingCampaign;
    if (fetchingCampaign) {
        return React.createElement(Loading, { size: "extraSmall" });
    }
    if (!campaign) {
        return null;
    }
    var id = campaign.id, shortName = campaign.shortName, type = campaign.type;
    var onClick = useCallback(function () {
        if (!campaign) {
            return;
        }
        openModal(id, shortName, 'CampaignModal');
    }, [campaign]);
    return (React.createElement(Clickable, { onClick: onClick },
        React.createElement(AttributionPill, null,
            React.createElement(Icon, { name: iconsByCampaign[type], width: "10px", height: "12px" }),
            campaign.shortName)));
};

var File = function (_a) {
    var _b;
    var file = _a.file, report = _a.report, indicator = _a.indicator, actor = _a.actor, eodCase = _a.eodCase, campaign = _a.campaign, fetchingReport = _a.fetchingReport, fetchingIndicator = _a.fetchingIndicator, fetchingActor = _a.fetchingActor, fetchingCase = _a.fetchingCase, fetchingCampaign = _a.fetchingCampaign, hasEodAccess = _a.hasEodAccess, boxProps = __rest(_a, ["file", "report", "indicator", "actor", "eodCase", "campaign", "fetchingReport", "fetchingIndicator", "fetchingActor", "fetchingCase", "fetchingCampaign", "hasEodAccess"]);
    var filename = file.filename, createdTime = file.createdTime, sha256 = file.sha256, sha1 = file.sha1, md5 = file.md5, size = file.size, owner = file.owner;
    var fetchingData = useMemo(function () {
        return fetchingReport || fetchingIndicator;
    }, [fetchingReport, fetchingIndicator]);
    var indeterminateScore = 50;
    var score = useMemo(function () { var _a, _b; return (_b = (_a = indicator === null || indicator === void 0 ? void 0 : indicator.indicatorScore) !== null && _a !== void 0 ? _a : report === null || report === void 0 ? void 0 : report.reportScore) !== null && _b !== void 0 ? _b : indeterminateScore; }, [indicator, report]);
    var malicious = useMemo(function () {
        if (fetchingData) {
            return false;
        }
        if (!score) {
            return false;
        }
        return score > 50;
    }, [fetchingData, score]);
    var type = useMemo(function () {
        if (!report) {
            return 'file';
        }
        return report.type;
    }, [report]);
    return (React.createElement(FileBox, __assign({}, boxProps),
        React.createElement(FileHeader, null,
            React.createElement(FileName, { filename: filename, md5: md5, malicious: malicious, fetchingData: fetchingData, type: type }),
            React.createElement(FileDate, { createdTime: createdTime })),
        React.createElement(FileSubHeader, null,
            (report === null || report === void 0 ? void 0 : report.status) === 'COMPLETED' && React.createElement(FileType, { type: type }),
            (report === null || report === void 0 ? void 0 : report.status) === 'COMPLETED' && (React.createElement(FileMaliciousContent, { type: type, malicious: malicious })),
            React.createElement(FileThreatActor, { fetchingData: fetchingActor, actor: actor }),
            React.createElement(FileMalwareFamilies, { fetchingData: fetchingData, malwareFamilies: (_b = indicator === null || indicator === void 0 ? void 0 : indicator.associations) === null || _b === void 0 ? void 0 : _b.malwareFamilies }),
            React.createElement(FileCampaign, { campaign: campaign, fetchingCampaign: fetchingCampaign }),
            hasEodAccess && (React.createElement(FileAssistance, null, fetchingCase ? (React.createElement(Loading, { size: "extraSmall" })) : eodCase ? (React.createElement(FileAssistanceCase, { id: eodCase.id, timestamp: eodCase.timestamp })) : (React.createElement(FileAssistanceRequest, { filename: filename, sha256: sha256, createdTime: createdTime, size: size }))))),
        React.createElement(FileBody, null,
            type === 'archive' ? (React.createElement(FileContents, { extractedFilesCount: (report === null || report === void 0 ? void 0 : report.extractedFilesCount) || 0 })) : (React.createElement(FileScoreAndStatus, { score: score, verdict: (indicator === null || indicator === void 0 ? void 0 : indicator.indicatorVerdict) || (report === null || report === void 0 ? void 0 : report.reportVerdict), reportStatus: report === null || report === void 0 ? void 0 : report.status, fetchingData: fetchingData })),
            React.createElement(FileMetadata, { size: size, owner: owner, mimeType: report === null || report === void 0 ? void 0 : report.mimeType, fetchingReport: fetchingReport, type: type }),
            React.createElement(FileHashes, { sha256: sha256, sha1: sha1, md5: md5 }))));
};

/**
 * Both of these functions are taken from:
 *
 * https://ghe.eng.fireeye.com/GSI-SDE-FIIP/nautilusjs/blob/e7279e930af550fa0012666ddb652a2ad1213bf2/packages/app/src/store/actors/normalizers.js
 */
var normalizeHierarchicalLocations = function (locations) {
    // For each location item, merges the lowest hierarchical location reference
    // object with a name (meaning it's known) into the base.  If no name is
    // found on the expected objects, filter out the item.
    return locations.reduce(function (acc, location) {
        var _a, _b, _c;
        if (!location) {
            return acc;
        }
        if ((_a = location.country) === null || _a === void 0 ? void 0 : _a.name) {
            location = __assign(__assign({}, location), location.country);
        }
        else if ((_b = location.sub_region) === null || _b === void 0 ? void 0 : _b.name) {
            location = __assign(__assign({}, location), location.sub_region);
        }
        else if ((_c = location.region) === null || _c === void 0 ? void 0 : _c.name) {
            location = __assign(__assign({}, location), location.region);
        }
        else {
            return acc;
        }
        return __spreadArray(__spreadArray([], acc, true), [location], false);
    }, []);
};
var normalizeActor = function (data) {
    var _a, _b, _c, _d, _e, _f;
    return {
        countryCode: (_d = (_c = (_b = (_a = data.locations) === null || _a === void 0 ? void 0 : _a.source) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.country) === null || _d === void 0 ? void 0 : _d.iso2,
        id: data.id,
        motivations: data.motivations || [],
        name: data.name,
        sourceLocations: normalizeHierarchicalLocations(((_e = data.locations) === null || _e === void 0 ? void 0 : _e.source) || []),
        targetLocations: ((_f = data.locations) === null || _f === void 0 ? void 0 : _f.target) || [],
    };
};

var normalizeCase = function (rawCase) {
    var id = rawCase.id, requested_date = rawCase.requested_date;
    var timestamp = format(parseISO(requested_date), 'MMMM d, yyyy');
    return {
        id: id,
        timestamp: timestamp,
    };
};

/**
 * Kept to support records that do not
 * have firstName and lastName
 *
 */
var convertEmailToOwnerInfo = function (email) {
    var username = ((email === null || email === void 0 ? void 0 : email.split('@')) || ['Unknown'])[0];
    var splitter = ['.', '+', '_'].filter(function (char) { return username.includes(char); })[0];
    var parts = username.split(splitter).map(function (str) { return capitalizeString(str); });
    var name = parts.join(' ');
    var initials = parts
        .map(function (str) { return str[0]; })
        .join('')
        .toUpperCase();
    return {
        name: name,
        initials: initials,
    };
};
var parseOwnerInfo = function (owner) {
    var firstName = owner.firstName, lastName = owner.lastName, email = owner.email;
    if (!firstName || !lastName) {
        return convertEmailToOwnerInfo(email);
    }
    var name = "".concat(capitalizeString(firstName), " ").concat(capitalizeString(lastName));
    var initials = "".concat(firstName[0]).concat(lastName[0]).toUpperCase();
    return {
        name: name,
        initials: initials,
    };
};

var getReportScore = function (verdict) {
    if (!verdict) {
        return null;
    }
    if (!(verdict === null || verdict === void 0 ? void 0 : verdict.match('malicious|benign'))) {
        return null;
    }
    return verdict === 'malicious' ? 100 : 0;
};

var getReportVerdict = function (maliciousDetermination, analysisDate) {
    if (!maliciousDetermination || !analysisDate) {
        return null;
    }
    var determination = (maliciousDetermination === null || maliciousDetermination === void 0 ? void 0 : maliciousDetermination.toLowerCase()) || '';
    if (determination.includes('unknown') || determination === '') {
        return null;
    }
    return {
        fileAnalysisVerdict: {
            verdict: determination.includes('malicious') ? 'malicious' : 'benign',
            timestamp: analysisDate,
        },
        authoritativeVerdict: 'fileAnalysisVerdict',
    };
};

var parseFilename = function (filename, sha256) {
    if (!filename.trim()) {
        return sha256;
    }
    return filename;
};

var normalizeFile = function (file) {
    var _a = file || {}, id = _a.id, rawFilename = _a.filename, createdTime = _a.createdTime, sha256 = _a.sha256, sha1 = _a.sha1, md5 = _a.md5, size = _a.size;
    var owner = parseOwnerInfo(file.owner || {});
    var filename = parseFilename(rawFilename, sha256);
    return {
        id: id,
        filename: filename,
        createdTime: createdTime,
        sha256: sha256,
        sha1: sha1,
        md5: md5,
        size: size,
        owner: owner,
    };
};

var normalizeIndicator = function (indicator) {
    var value = indicator.value, indicatorScore = indicator.mscore, _a = indicator.attributed_associations, rawAssociations = _a === void 0 ? [] : _a, indicatorVerdict = indicator.verdict, _b = indicator.campaigns, rawCampaigns = _b === void 0 ? [] : _b;
    var _c = (rawAssociations || []).reduce(function (acc, association) {
        var id = association.id, name = association.name;
        if (association.type === 'threat-actor') {
            acc.threatActors.push(id);
        }
        if (association.type === 'malware') {
            acc.malwareFamilies.push({ id: id, name: name });
        }
        return acc;
    }, { threatActors: [], malwareFamilies: [] }), threatActors = _c.threatActors, malwareFamilies = _c.malwareFamilies;
    var campaigns = (rawCampaigns === null || rawCampaigns === void 0 ? void 0 : rawCampaigns.map(function (_a) {
        var id = _a.id;
        return id;
    })) || [];
    return {
        value: value,
        indicatorScore: indicatorScore,
        associations: {
            threatActors: threatActors,
            malwareFamilies: malwareFamilies,
            campaigns: campaigns,
        },
        indicatorVerdict: indicatorVerdict,
    };
};

var normalizeReport = function (report) {
    var _a;
    if (report === void 0) { report = {}; }
    var mimeType = report.mimeType, _b = report.generated_on, lastAnalyzed = _b === void 0 ? null : _b, _c = report.extractedFiles, extractedFiles = _c === void 0 ? [] : _c;
    var isAnalyzing = report.status === "IN_PROGRESS" || report.status === "SUBMITTED";
    var status = isAnalyzing ? 'ANALYZING' : 'COMPLETED';
    var reportVerdict = getReportVerdict(report === null || report === void 0 ? void 0 : report.maliciousDetermination, lastAnalyzed);
    var reportScore = getReportScore((_a = reportVerdict === null || reportVerdict === void 0 ? void 0 : reportVerdict.fileAnalysisVerdict) === null || _a === void 0 ? void 0 : _a.verdict);
    var type = (extractedFiles === null || extractedFiles === void 0 ? void 0 : extractedFiles.length) > 0 ? 'archive' : 'file';
    var extractedFilesCount = (extractedFiles === null || extractedFiles === void 0 ? void 0 : extractedFiles.length) || 0;
    return {
        status: status,
        mimeType: mimeType,
        lastAnalyzed: lastAnalyzed,
        reportScore: reportScore,
        reportVerdict: reportVerdict,
        type: type,
        extractedFilesCount: extractedFilesCount,
    };
};

export { File, normalizeActor, normalizeCase, normalizeFile, normalizeIndicator, normalizeReport };
